/**
 * recommendPhasePowerPlan — the deterministic per-phase power recommendation.
 *
 * Converges, per phase (ascending sequence, cumulative-at-completion):
 *   load target range   (effectivePhaseCapacity — exact ⊕ bucket)
 * × per-source ranges   (effectivePhaseSourceCapacity — exact ⊕ min–max)
 * × availability        (resolvePerSourceAvailability — grid/gas grounded,
 *                        undocumented is never unlimited)
 * × site max envelope   (the max-power stamp, when fresh)
 * → a recommended cumulative load MW + per-source split, with named binders,
 *   honest shortfall findings, and an adequacy verdict ROUND-TRIPPED through
 *   `assessPhaseFeasibility` (no parallel physics — the grader is the SSOT).
 *
 * ENGINEERING RULES (binding):
 *   - DETERMINISTIC. No Date/random; merit order from `sourceEconomicsFor`
 *     (indicative screening $/MWh, disclosed as such), ties broken by the
 *     canonical PHASE_POWER_COMPONENT_KEYS order.
 *   - NEVER fabricate feasibility: when documented+declared supply cannot
 *     serve the phase's declared minimum, the engine recommends the SERVABLE
 *     load and emits a shortfall finding — it never claims the gap closed.
 *   - EXACT DECLARATIONS ARE FIXED: an exact per-source MW is a user
 *     commitment (floor = ceiling); the engine only sizes within RANGES.
 *   - User floors are statements: floors above availability → 'conflict',
 *     never a silent shrink.
 *   - All MW are cumulative-at-phase-completion on the raw capacity axis
 *     (the same axis `assessPhaseFeasibility` grades).
 *
 * UNITS: MW (capacity), % (CF / capacity credit), USD/MWh (merit ranking).
 */
import type {
  PhasePowerComponentKey,
  ProjectIntent,
  ProjectPhase,
} from '../../types/siteRag';
import { PHASE_POWER_COMPONENT_KEYS } from '../../types/siteRag';
import {
  effectivePhaseCapacity,
  effectivePhaseSourceCapacity,
  effectivePower,
  effectivePowerMwRange,
} from '../projectIntent';
import {
  assessPhaseFeasibility,
  baseloadEnergyCfPct,
  classifyPhaseComponent,
  DEFAULT_CAPACITY_CREDIT_PCT,
  phaseComponentToTechnologyId,
  RESERVE_MARGIN_PCT,
  type PhaseFeasibility,
} from '../phaseFeasibility';
import { sourceEconomicsFor } from '../finDecision/presets/shared';
import { SCREENING_FIXED_CHARGE_RATE } from '../generationImpact/screeningEconomics';
import type {
  PerSourceAvailabilityResolution,
  SourceAvailabilityStatus,
} from './resolvePerSourceAvailability';

const TOL_MW = 1e-6;
const LOAD_STEP_MW = 0.1;
const HOURS_PER_YEAR = 8760;

export type PhasePlanBinder =
  | 'user-range'
  | 'grid-availability'
  | 'gas-availability'
  | 'site-envelope'
  | 'goal-range'
  | 'prior-phase-floor';

export interface PhasePlanPerSource {
  key: PhasePowerComponentKey;
  /** Recommended CUMULATIVE MW at phase completion. */
  recommendedMW: number;
  /** CF the plan grades with — declared, else the baseload-energy default. */
  recommendedCapacityFactorPct: number;
  floorMW: number;
  floorWhy: 'user-min' | 'user-exact' | 'prior-phase' | 'none';
  ceilingMW: number | null;
  ceilingWhy:
    | 'user-max'
    | 'user-exact'
    | 'grid-availability'
    | 'gas-pool'
    | 'no-growth-undocumented'
    | null;
  availabilityStatus: SourceAvailabilityStatus;
  disclosures: string[];
}

export type PhasePlanEntryStatus = 'recommended' | 'not-recommendable' | 'conflict';

export type PhasePlanReason =
  | 'load-range-undecided'
  | 'or-mode-no-combined-claim'
  | 'no-components'
  | 'floors-exceed-availability'
  | 'range-conflicts-envelope'
  | 'range-conflicts-prior-phase';

export interface PhasePlanEntry {
  phaseId: string;
  phaseLabel: string;
  status: PhasePlanEntryStatus;
  reasons: PhasePlanReason[];
  /** Recommended CUMULATIVE load MW; null unless status === 'recommended'. */
  recommendedLoadMW: number | null;
  loadRange: { min: number; max: number } | null;
  binders: PhasePlanBinder[];
  /** Documented supply serves less than the phase's declared minimum. */
  shortfall: { belowPhaseMinMW: number } | null;
  perSource: PhasePlanPerSource[];
  /** assessPhaseFeasibility on the candidate phase — verbatim, no re-derivation. */
  adequacy: PhaseFeasibility | null;
  disclosures: string[];
}

export interface PhasePowerPlanCampus {
  ultimateRecommendedMW: number | null;
  goalRange: { min: number | null; max: number | null } | null;
  goalStatus: 'within' | 'below' | 'above' | 'no-goal';
}

export type PhasePowerPlanResolution =
  | {
      status: 'available';
      perPhase: PhasePlanEntry[];
      campus: PhasePowerPlanCampus;
      disclosures: string[];
    }
  | { status: 'unavailable'; reasons: Array<'no-intent' | 'no-phases'> };

export interface RecommendPhasePowerPlanArgs {
  intent: ProjectIntent | null | undefined;
  availability: PerSourceAvailabilityResolution;
  /** The max-power envelope: the intent stamp + its resolved staleness.
   *  Stale or absent → the envelope is DISCLOSED as unapplied, never guessed. */
  maxPowerEnvelope?: {
    facilityMW: number | null;
    stalenessReason: string | null;
  } | null;
}

interface SourceBound {
  key: PhasePowerComponentKey;
  floor: number;
  cap: number; // Infinity = unbounded by this source's own constraints
  floorWhy: PhasePlanPerSource['floorWhy'];
  capWhy: PhasePlanPerSource['ceilingWhy'];
  cfPct: number;
  creditPct: number;
  isStorage: boolean;
  inGasPool: boolean;
  availabilityStatus: SourceAvailabilityStatus;
  disclosures: string[];
  meritUsdPerMwh: number;
}

function round1(v: number): number {
  return Math.round(v * 10) / 10;
}

function floorToStep(v: number): number {
  return Math.floor(v / LOAD_STEP_MW) * LOAD_STEP_MW;
}

/**
 * Indicative screening $/MWh for merit ordering — capital (FCR-annualised)
 * + fixed O&M spread over CF-hours + variable O&M + fuel. Dimensional:
 * [USD/kW]×[1/yr]×[kW/MW] ÷ [h/yr] = USD/MWh. Disclosed as screening-grade.
 */
function meritCostUsdPerMwh(key: PhasePowerComponentKey, cfPct: number): number {
  const tech = phaseComponentToTechnologyId(key);
  if (tech == null) return Number.POSITIVE_INFINITY; // battery — never merit-filled for energy
  const econ = sourceEconomicsFor(tech);
  const cfFrac = Math.max(cfPct, 1) / 100;
  const cfHours = cfFrac * HOURS_PER_YEAR;
  return (
    (econ.capexPerKwUsd * SCREENING_FIXED_CHARGE_RATE * 1000) / cfHours +
    (econ.fixedOmPerKwYearUsd * 1000) / cfHours +
    econ.variableOmPerMwhUsd +
    econ.fuelCostPerMwhUsd
  );
}

/** Deterministic merit order: ascending $/MWh, canonical order on ties. */
function meritOrder(bounds: SourceBound[]): SourceBound[] {
  const canonical = (k: PhasePowerComponentKey) => PHASE_POWER_COMPONENT_KEYS.indexOf(k);
  return [...bounds].sort((a, b) =>
    a.meritUsdPerMwh !== b.meritUsdPerMwh
      ? a.meritUsdPerMwh - b.meritUsdPerMwh
      : canonical(a.key) - canonical(b.key),
  );
}

interface AllocationResult {
  alloc: Map<PhasePowerComponentKey, number>;
  firmMW: number;
  energyMW: number;
}

/**
 * Fill sources from their floors toward `targetFirmMW`/`targetEnergyMW` in
 * merit order, honouring per-source caps and the shared gas pool. Generators
 * address both tiers; storage tops up FIRM only, after generators exhaust
 * (its merit is +Infinity). Passing Infinity targets fills everything to its
 * ceilings — that is how the servable supply is measured.
 */
function allocate(
  bounds: SourceBound[],
  targetFirmMW: number,
  targetEnergyMW: number,
  gasPoolMW: number | null,
): AllocationResult {
  const alloc = new Map<PhasePowerComponentKey, number>();
  for (const b of bounds) alloc.set(b.key, b.floor);

  const gasUsed = () =>
    bounds.filter(b => b.inGasPool).reduce((s, b) => s + (alloc.get(b.key) ?? 0), 0);
  const firm = () =>
    bounds.reduce((s, b) => s + (alloc.get(b.key) ?? 0) * (b.creditPct / 100), 0);
  const energy = () =>
    bounds.reduce((s, b) => s + (b.isStorage ? 0 : (alloc.get(b.key) ?? 0) * (b.cfPct / 100)), 0);

  const ordered = meritOrder(bounds);
  // Bounded loop: each pass either closes a deficit or exhausts a source.
  for (let guard = 0; guard < bounds.length * 4 + 8; guard += 1) {
    const firmDef = targetFirmMW - firm();
    const energyDef = targetEnergyMW - energy();
    if (firmDef <= TOL_MW && energyDef <= TOL_MW) break;

    const pick = ordered.find(b => {
      const cur = alloc.get(b.key) ?? 0;
      let headroom = b.cap - cur;
      if (b.inGasPool && gasPoolMW != null) {
        headroom = Math.min(headroom, gasPoolMW - gasUsed());
      }
      if (headroom <= TOL_MW) return false;
      // Storage addresses firm only; generators address either open deficit.
      return b.isStorage ? firmDef > TOL_MW : firmDef > TOL_MW || energyDef > TOL_MW;
    });
    if (!pick) break; // nothing left that can address an open deficit

    const cur = alloc.get(pick.key) ?? 0;
    let headroom = pick.cap - cur;
    if (pick.inGasPool && gasPoolMW != null) headroom = Math.min(headroom, gasPoolMW - gasUsed());
    const needFirm = firmDef > TOL_MW ? firmDef / (pick.creditPct / 100) : 0;
    const needEnergy =
      !pick.isStorage && energyDef > TOL_MW ? energyDef / (pick.cfPct / 100) : 0;
    const delta = Math.min(headroom, Math.max(needFirm, needEnergy));
    if (delta <= TOL_MW) break;
    alloc.set(pick.key, cur + delta);
  }

  return { alloc, firmMW: firm(), energyMW: energy() };
}

export function recommendPhasePowerPlan(
  args: RecommendPhasePowerPlanArgs,
): PhasePowerPlanResolution {
  const intent = args.intent;
  if (!intent) return { status: 'unavailable', reasons: ['no-intent'] };
  const phases = (intent.phases ?? []).slice().sort((a, b) => a.sequence - b.sequence);
  if (phases.length === 0) return { status: 'unavailable', reasons: ['no-phases'] };

  const planDisclosures: string[] = [];
  const envelope = args.maxPowerEnvelope;
  const envelopeCap =
    envelope != null && envelope.facilityMW != null && envelope.stalenessReason == null
      ? envelope.facilityMW
      : null;
  if (envelope != null && envelope.facilityMW != null && envelope.stalenessReason != null) {
    planDisclosures.push(
      `Max-power envelope NOT applied — the stamp is stale (${envelope.stalenessReason}). The plan is bounded by declared ranges and availability only.`,
    );
  }
  const gasPool = args.availability.sharedConstraints.find(c => c.id === 'gas-fuel-pool') ?? null;

  const perPhase: PhasePlanEntry[] = [];
  let prevLoad = 0;
  const prevCum = new Map<PhasePowerComponentKey, number>();

  const updateFloorsFromExisting = (phase: ProjectPhase) => {
    // A skipped/existing phase still advances the monotonic floors so the
    // chain never breaks: cumulative capacity can only carry forward.
    const cap = effectivePhaseCapacity(phase);
    if (cap.isExact && cap.mw != null) prevLoad = Math.max(prevLoad, cap.mw);
    for (const key of phase.powerSourceConfig?.components ?? []) {
      const sc = effectivePhaseSourceCapacity(phase, key);
      if (sc.isExact && sc.mw != null) {
        prevCum.set(key, Math.max(prevCum.get(key) ?? 0, sc.mw));
      }
    }
  };

  for (const phase of phases) {
    const cfg = phase.powerSourceConfig;
    const entryBase = { phaseId: phase.id, phaseLabel: phase.label };

    // ── Eligibility ────────────────────────────────────────────────────
    const reasons: PhasePlanReason[] = [];
    if (!cfg || cfg.components.length === 0) reasons.push('no-components');
    // The absent-mixMode default is a known cross-module inconsistency —
    // the engine requires an EXPLICIT 'and' (an OR mix carries no
    // combined-capacity claim to size against).
    if (cfg && cfg.mixMode !== 'and') reasons.push('or-mode-no-combined-claim');
    const loadCap = effectivePhaseCapacity(phase);
    if (loadCap.range == null) reasons.push('load-range-undecided');
    if (reasons.length > 0) {
      perPhase.push({
        ...entryBase,
        status: 'not-recommendable',
        reasons,
        recommendedLoadMW: null,
        loadRange: null,
        binders: [],
        shortfall: null,
        perSource: [],
        adequacy: null,
        disclosures: [],
      });
      updateFloorsFromExisting(phase);
      continue;
    }

    // ── Load window (cumulative) ───────────────────────────────────────
    const rangeMin = Number.isFinite(loadCap.range!.min) ? loadCap.range!.min : 0;
    const rangeMax = loadCap.range!.max; // Infinity on open-high buckets
    const lo = Math.max(rangeMin, prevLoad);
    const hi = Math.min(rangeMax, envelopeCap ?? Number.POSITIVE_INFINITY);
    if (lo > hi + TOL_MW) {
      const conflictReasons: PhasePlanReason[] = [];
      if (envelopeCap != null && envelopeCap < rangeMin) conflictReasons.push('range-conflicts-envelope');
      if (prevLoad > rangeMax) conflictReasons.push('range-conflicts-prior-phase');
      if (conflictReasons.length === 0) conflictReasons.push('range-conflicts-envelope');
      perPhase.push({
        ...entryBase,
        status: 'conflict',
        reasons: conflictReasons,
        recommendedLoadMW: null,
        loadRange: { min: rangeMin, max: rangeMax },
        binders: [],
        shortfall: null,
        perSource: [],
        adequacy: null,
        disclosures: [],
      });
      updateFloorsFromExisting(phase);
      continue;
    }

    // ── Per-source bounds ──────────────────────────────────────────────
    const bounds: SourceBound[] = [];
    let floorsExceed = false;
    for (const key of cfg!.components) {
      const sc = effectivePhaseSourceCapacity(phase, key);
      const avail = args.availability.perSource[key];
      const declaredCf = cfg!.componentParams?.[key]?.capacityFactorPct;
      // Firm sources graded at their credit-tier CF unless declared — the
      // same workaround feasibleTechnologiesForIntent uses, else a firm
      // source absurdly fails the energy tier at its market CF.
      const cfPct =
        typeof declaredCf === 'number' && Number.isFinite(declaredCf) && declaredCf > 0
          ? declaredCf
          : baseloadEnergyCfPct(key);
      const creditPct = DEFAULT_CAPACITY_CREDIT_PCT[key];
      const isStorage = classifyPhaseComponent(key) === 'storage';
      const carried = prevCum.get(key) ?? 0;
      const disclosures = [...(avail?.disclosures ?? [])];

      let floor: number;
      let cap: number;
      let floorWhy: PhasePlanPerSource['floorWhy'];
      let capWhy: PhasePlanPerSource['ceilingWhy'];
      if (sc.kind === 'exact' && sc.mw != null) {
        // A declared exact is a user COMMITMENT — fixed, not negotiable.
        floor = Math.max(sc.mw, carried);
        cap = floor;
        floorWhy = 'user-exact';
        capWhy = 'user-exact';
      } else {
        const userMin = sc.kind === 'range' ? sc.minMW ?? 0 : 0;
        const userMax = sc.kind === 'range' ? sc.maxMW : null;
        floor = Math.max(userMin, carried);
        floorWhy = userMin > carried ? 'user-min' : carried > 0 ? 'prior-phase' : 'none';
        const availCap =
          avail != null && avail.availableMW != null && !isStorage ? avail.availableMW : null;
        if (userMax != null && (availCap == null || userMax <= availCap)) {
          cap = userMax;
          capWhy = 'user-max';
        } else if (availCap != null) {
          cap = availCap;
          capWhy = avail?.sharedConstraintId === 'gas-fuel-pool' ? 'gas-pool' : 'grid-availability';
        } else if (userMax != null) {
          cap = userMax;
          capWhy = 'user-max';
        } else if (avail?.status === 'undocumented' && !isStorage) {
          // No range AND no documented availability: the engine never adds
          // MW on nothing — the source may keep what it already carries.
          cap = floor;
          capWhy = 'no-growth-undocumented';
        } else {
          cap = Number.POSITIVE_INFINITY;
          capWhy = null;
        }
      }
      if (floor > cap + TOL_MW) floorsExceed = true;

      bounds.push({
        key,
        floor,
        cap,
        floorWhy,
        capWhy,
        cfPct,
        creditPct,
        isStorage,
        inGasPool: avail?.sharedConstraintId === 'gas-fuel-pool',
        availabilityStatus: avail?.status ?? 'undocumented',
        disclosures,
        meritUsdPerMwh: isStorage ? Number.POSITIVE_INFINITY : meritCostUsdPerMwh(key, cfPct),
      });
    }
    const gasPoolMW = gasPool?.capMW ?? null;
    const gasFloors = bounds.filter(b => b.inGasPool).reduce((s, b) => s + b.floor, 0);
    if (gasPoolMW != null && gasFloors > gasPoolMW + TOL_MW) floorsExceed = true;
    if (floorsExceed) {
      perPhase.push({
        ...entryBase,
        status: 'conflict',
        reasons: ['floors-exceed-availability'],
        recommendedLoadMW: null,
        loadRange: { min: rangeMin, max: rangeMax },
        binders: [],
        shortfall: null,
        perSource: bounds.map(b => boundToPerSource(b, b.floor)),
        adequacy: null,
        disclosures: ['Declared minimums exceed documented availability — the engine never silently shrinks a user floor. Reduce the floors or document more capacity.'],
      });
      updateFloorsFromExisting(phase);
      continue;
    }

    // ── Servable load: fill everything to its ceilings, then invert ────
    const atCeilings = allocate(bounds, Number.POSITIVE_INFINITY, Number.POSITIVE_INFINITY, gasPoolMW);
    // servable = min(firm ÷ (1+reserve), energy) — MW of 24/7 load the
    // documented+declared supply can carry with the planning reserve.
    const servableRaw = Math.min(
      atCeilings.firmMW / (1 + RESERVE_MARGIN_PCT / 100),
      atCeilings.energyMW,
    );
    const servable = floorToStep(servableRaw);

    // ── Recommended load + binders ─────────────────────────────────────
    let recommendedLoad: number;
    let shortfall: PhasePlanEntry['shortfall'] = null;
    const binders: PhasePlanBinder[] = [];
    if (servable < lo - TOL_MW) {
      // Honesty over optimism: recommend what the supply actually serves and
      // flag the gap against the phase's own declared minimum window.
      recommendedLoad = Math.max(servable, 0);
      shortfall = { belowPhaseMinMW: round1(lo - recommendedLoad) };
      const limiting = limitingSupplyBinders(bounds, gasPoolMW);
      binders.push(...limiting);
    } else {
      recommendedLoad = Math.min(hi, Math.max(lo, servable));
      if (recommendedLoad === hi && servable > hi - TOL_MW) {
        binders.push(hi === rangeMax ? 'user-range' : 'site-envelope');
      } else if (recommendedLoad < hi - TOL_MW) {
        binders.push(...limitingSupplyBinders(bounds, gasPoolMW));
      }
      if (recommendedLoad === lo && lo > rangeMin + TOL_MW) binders.push('prior-phase-floor');
    }
    recommendedLoad = round1(recommendedLoad);

    // ── Allocation to serve the recommended load ───────────────────────
    const targetFirm = recommendedLoad * (1 + RESERVE_MARGIN_PCT / 100);
    const targetEnergy = recommendedLoad;
    const { alloc } = allocate(bounds, targetFirm, targetEnergy, gasPoolMW);
    for (const b of bounds) alloc.set(b.key, round1(alloc.get(b.key) ?? 0));

    // ── Adequacy round-trip (the grader is the SSOT) ───────────────────
    // Ephemeral candidate for the grader only (never persisted) — the exact
    // capacityMW wins over any stored bucket by the resolver's own precedence.
    const buildCandidate = (): ProjectPhase => ({
      ...phase,
      capacityMW: recommendedLoad,
      powerSourceConfig: {
        ...cfg!,
        mixMode: 'and',
        componentParams: Object.fromEntries(
          bounds.map(b => [
            b.key,
            {
              capacityMW: alloc.get(b.key) ?? 0,
              capacityFactorPct: b.cfPct,
            },
          ]),
        ),
      },
    });
    let adequacy = recommendedLoad > 0 ? assessPhaseFeasibility(buildCandidate()) : null;
    if (adequacy != null && adequacy.verdict !== 'feasible') {
      // Deterministic single retry: rounding can shave the reserve — bump the
      // last merit-ordered source with headroom by one step and re-verify.
      const ordered = meritOrder(bounds);
      for (let i = ordered.length - 1; i >= 0; i -= 1) {
        const b = ordered[i]!;
        const cur = alloc.get(b.key) ?? 0;
        if (cur + LOAD_STEP_MW <= b.cap + TOL_MW) {
          alloc.set(b.key, round1(cur + LOAD_STEP_MW));
          adequacy = assessPhaseFeasibility(buildCandidate());
          break;
        }
      }
    }

    const entryDisclosures: string[] = [];
    if (bounds.some(b => b.availabilityStatus === 'proxy')) {
      entryDisclosures.push(
        'Grid availability is a voltage-class PROXY — not nameplate ATC; utility confirmation refines this plan.',
      );
    }
    entryDisclosures.push(
      `Merit order is an INDICATIVE screening ranking (FCR ${Math.round(SCREENING_FIXED_CHARGE_RATE * 100)}%/yr, benchmark costs) — not a pro forma.`,
    );

    perPhase.push({
      ...entryBase,
      status: 'recommended',
      reasons: [],
      recommendedLoadMW: recommendedLoad,
      loadRange: { min: rangeMin, max: rangeMax },
      binders: [...new Set(binders)],
      shortfall,
      perSource: bounds.map(b => boundToPerSource(b, alloc.get(b.key) ?? 0)),
      adequacy,
      disclosures: entryDisclosures,
    });

    // Advance the monotonic chain with the RECOMMENDED figures.
    prevLoad = Math.max(prevLoad, recommendedLoad);
    for (const b of bounds) {
      prevCum.set(b.key, Math.max(prevCum.get(b.key) ?? 0, alloc.get(b.key) ?? 0));
    }
  }

  // ── Campus rollup vs the site goal range (a reported cross-check — the
  //    final phase is never silently clamped to it) ─────────────────────
  const ultimate = [...perPhase].reverse().find(e => e.status === 'recommended') ?? null;
  const ep = effectivePower(intent);
  const goalRangeRaw = ep.isExact
    ? intent.powerRequirementMW != null
      ? { min: intent.powerRequirementMW, max: intent.powerRequirementMW }
      : null
    : effectivePowerMwRange(ep);
  const positiveOrNull = (v: number | null | undefined): number | null =>
    typeof v === 'number' && Number.isFinite(v) && v > 0 ? v : null;
  const goalRange = goalRangeRaw
    ? { min: positiveOrNull(goalRangeRaw.min), max: positiveOrNull(goalRangeRaw.max) }
    : null;
  let goalStatus: PhasePowerPlanCampus['goalStatus'] = 'no-goal';
  if (goalRange && ultimate?.recommendedLoadMW != null) {
    const mw = ultimate.recommendedLoadMW;
    if (goalRange.min != null && mw < goalRange.min - TOL_MW) goalStatus = 'below';
    else if (goalRange.max != null && mw > goalRange.max + TOL_MW) goalStatus = 'above';
    else goalStatus = 'within';
  }
  if (goalStatus === 'below' || goalStatus === 'above') {
    planDisclosures.push(
      `The recommended ultimate build (${ultimate?.recommendedLoadMW} MW) is ${goalStatus} the site's declared goal range — reconcile the phase ranges with the campus goal rather than adopting either silently.`,
    );
  }

  return {
    status: 'available',
    perPhase,
    campus: {
      ultimateRecommendedMW: ultimate?.recommendedLoadMW ?? null,
      goalRange,
      goalStatus,
    },
    disclosures: planDisclosures,
  };
}

function boundToPerSource(b: SourceBound, mw: number): PhasePlanPerSource {
  return {
    key: b.key,
    recommendedMW: round1(mw),
    recommendedCapacityFactorPct: b.cfPct,
    floorMW: round1(b.floor),
    floorWhy: b.floorWhy,
    ceilingMW: Number.isFinite(b.cap) ? round1(b.cap) : null,
    ceilingWhy: b.capWhy,
    availabilityStatus: b.availabilityStatus,
    disclosures: b.disclosures,
  };
}

/** Which supply-side constraint(s) limited the servable load. */
function limitingSupplyBinders(
  bounds: SourceBound[],
  gasPoolMW: number | null,
): PhasePlanBinder[] {
  const binders = new Set<PhasePlanBinder>();
  const gasAtPool =
    gasPoolMW != null &&
    bounds.filter(b => b.inGasPool).reduce((s, b) => s + Math.min(b.cap, Number.MAX_SAFE_INTEGER), 0) >
      gasPoolMW - TOL_MW;
  for (const b of bounds) {
    if (!Number.isFinite(b.cap)) continue;
    switch (b.capWhy) {
      case 'grid-availability':
        binders.add('grid-availability');
        break;
      case 'gas-pool':
        binders.add('gas-availability');
        break;
      case 'user-max':
      case 'user-exact':
      case 'no-growth-undocumented':
        binders.add('user-range');
        break;
      default:
        break;
    }
  }
  if (gasAtPool) binders.add('gas-availability');
  return [...binders];
}
